import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { buildIncidentRpcArgs, type IncidentPetRole, type IncidentParticipantDraft } from "./stayIncidentModel";

export interface StayObservationType {
  id: string;
  name: string;
  color: string | null;
  icon: string | null;
  isIncident: boolean;
}

export interface StayIncidentParticipant {
  petId: string;
  petName: string;
  photoPath: string | null;
}

export interface StayObservation {
  id: string;
  description: string;
  loggedAt: string | null;
  typeName: string;
  typeColor: string | null;
  isIncident: boolean;
  participants: StayIncidentParticipant[];
}

export const stayObservationTypesKey = (clinicId: string | null) => ["stay-observation-types", clinicId];
export const stayObservationsKey = (reservationId: string | null, day: string) =>
  ["stay-observations", reservationId, day];

export function useStayObservationTypes() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: stayObservationTypesKey(clinicId),
    enabled: !!clinicId,
    queryFn: async (): Promise<StayObservationType[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("stay_reservation_daily_observation_types")
        .select("id, name, color, icon, is_incident")
        .eq("clinic_id", clinicId)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      if ((data ?? []).length === 0) {
        const { error: seedError } = await supabase
          .from("stay_reservation_daily_observation_types")
          .insert([
            { clinic_id: clinicId, name: "Observação", is_incident: false, sort_order: 10 },
            { clinic_id: clinicId, name: "Incidente", is_incident: true, sort_order: 20 },
          ]);
        if (!seedError) {
          const seeded = await supabase
            .from("stay_reservation_daily_observation_types")
            .select("id, name, color, icon, is_incident")
            .eq("clinic_id", clinicId)
            .eq("is_active", true)
            .order("sort_order");
          if (!seeded.error) return (seeded.data ?? []).map((row) => ({
            id: row.id,
            name: row.name,
            color: row.color,
            icon: row.icon,
            isIncident: row.is_incident,
          }));
        }
      }
      return (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        color: row.color,
        icon: row.icon,
        isIncident: row.is_incident,
      }));
    },
  });
}

interface ObservationRow {
  id: string;
  description: string | null;
  logged_at: string | null;
  stay_reservation_daily_observation_types: {
    name: string;
    color: string | null;
    is_incident: boolean;
  } | null;
  stay_incident_pets: {
    pet_id: string;
    pets: { name: string | null; pet_photos: { file_path: string }[] | null } | null;
  }[] | null;
}

const toObservation = (row: ObservationRow): StayObservation => ({
  id: row.id,
  description: row.description ?? "",
  loggedAt: row.logged_at,
  typeName: row.stay_reservation_daily_observation_types?.name ?? "",
  typeColor: row.stay_reservation_daily_observation_types?.color ?? null,
  isIncident: row.stay_reservation_daily_observation_types?.is_incident ?? false,
  participants: (row.stay_incident_pets ?? []).map((participant) => ({
    petId: participant.pet_id,
    petName: participant.pets?.name?.trim() || participant.pet_id,
    photoPath: participant.pets?.pet_photos?.[0]?.file_path ?? null,
  })),
});

export function useStayObservations(reservationId: string | null, day: string) {
  return useQuery({
    queryKey: stayObservationsKey(reservationId, day),
    enabled: !!reservationId && !!day,
    queryFn: async (): Promise<StayObservation[]> => {
      if (!reservationId) return [];
      const { data, error } = await supabase
        .from("stay_reservation_daily_observations")
        .select(
          `id, description, logged_at,
           stay_reservation_daily_observation_types ( name, color, is_incident ),
           stay_incident_pets ( pet_id, pets ( name, pet_photos ( file_path ) ) )`,
        )
        .eq("stay_reservation_id", reservationId)
        .eq("log_date", day)
        .order("logged_at", { nullsFirst: false });
      if (error) throw error;
      return ((data as unknown as ObservationRow[]) ?? []).map(toObservation);
    },
  });
}

export interface CreateObservationInput {
  reservationId: string;
  day: string;
  time: string;
  typeId: string;
  description: string;
}

export function useCreateStayObservation() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async (input: CreateObservationInput) => {
      const { error } = await supabase.from("stay_reservation_daily_observations").insert({
        stay_reservation_id: input.reservationId,
        stay_reservation_daily_observation_type_id: input.typeId,
        log_date: input.day,
        logged_at: input.time,
        description: input.description,
        created_by: profile?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: stayObservationsKey(variables.reservationId, variables.day) });
      queryClient.invalidateQueries({ queryKey: ["pet-stay-history"] });
    },
  });
}

export interface CreateIncidentInput {
  reservationId: string;
  day: string;
  time: string;
  typeId: string;
  description: string;
  primaryPetRole: IncidentPetRole;
  participants: IncidentParticipantDraft[];
}

export function useCreateStayIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateIncidentInput) => {
      const args = buildIncidentRpcArgs(input);
      const rpcClient = supabase as unknown as {
        rpc: (name: "create_stay_incident", params: typeof args) => Promise<{ data: string | null; error: Error | null }>;
      };
      const { data, error } = await rpcClient.rpc("create_stay_incident", args);
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: stayObservationsKey(variables.reservationId, variables.day) });
      queryClient.invalidateQueries({ queryKey: ["pet-stay-history"] });
      queryClient.invalidateQueries({ queryKey: ["pet-reservation-summary"] });
    },
  });
}
